import * as THREE from 'three';
import { EOpenPoseBodyBone } from './enums/e_open_pose_body_bone';
import { EOpenPoseBodyGroup } from './enums/e_open_pose_body_group';
import { EOpenPoseBodyNode } from './enums/e_open_pose_body_node';
import { to_hex_color } from './open_pose_body/open_pose_body_utils';

export class ConstantsOpenPose {
  /*
   * AMBIENT LIGHT
   */
  static readonly AMBIENT_LIGHT_COLOR = 0xffffff;
  static readonly AMBIENT_LIGHT_INTENSITY = 0.5;

  /*
   * AXES HELPER
   */
  static readonly AXES_HELPER_SIZE = 1000;

  /*
   * AXIS CURSOR
   */
  static readonly AXIS_CURSOR_AXIS_LENGTH_WORLD = 15;
  static readonly AXIS_CURSOR_CONE_HEIGHT_WORLD = 1.5;
  static readonly AXIS_CURSOR_CONE_POSTFIX_1 = '_CONE_1';
  static readonly AXIS_CURSOR_CONE_POSTFIX_2 = '_CONE_2';
  static readonly AXIS_CURSOR_CONE_RADIUS_WORLD = 0.4;
  static readonly AXIS_CURSOR_CYLINDER_POSTFIX = '_CYLINDER';
  static readonly AXIS_CURSOR_CYLINDER_RADIUS_WORLD = 0.2;
  static readonly AXIS_CURSOR_DIAMETER_MULTIPLIER = 2;
  static readonly AXIS_CURSOR_EPSILON_SMALL = 0.01;
  static readonly AXIS_CURSOR_NAME = 'AxisCursor';
  static readonly AXIS_CURSOR_PLANE_SCALE_MULTIPLIER = 1.5;
  static readonly AXIS_CURSOR_PLANE_SIZE_WORLD = 2;
  static readonly AXIS_CURSOR_X_AXIS_COLOR = 0xff4444;
  static readonly AXIS_CURSOR_XY_PLANE_COLOR = 0xff4444;
  static readonly AXIS_CURSOR_XZ_PLANE_COLOR = 0x4444ff;
  static readonly AXIS_CURSOR_Y_AXIS_COLOR = 0x44ff44;
  static readonly AXIS_CURSOR_YZ_PLANE_COLOR = 0x44ff44;
  static readonly AXIS_CURSOR_Z_AXIS_COLOR = 0x4444ff;

  /*
   * BASE BODY
   */
  static readonly BASE_BODY_HEIGHT = 46;
  static readonly BASE_BODY_WIDTH = 34;

  /*
   * BILLBOARD
   */
  static readonly BILLBOARD_DEFAULT_COLOR = 0xffffff;
  static readonly BILLBOARD_DIMENSIONS_HEIGHT = 400;
  static readonly BILLBOARD_DIMENSIONS_WIDTH = 400;
  static readonly BILLBOARD_EIGHT_NEIGHBOR_OFFSETS: Array<[number, number]> = [
    [-1, -1],
    [0, -1],
    [1, -1],
    [-1, 0],
    [1, 0],
    [-1, 1],
    [0, 1],
    [1, 1],
  ];
  static readonly BILLBOARD_NAME = 'billboard';
  static readonly BILLBOARD_POSITION_Y = -160;

  static readonly BILLBOARD_BLUR_PX = 6;
  static readonly BILLBOARD_BORDER_WIDTH = 2;
  static readonly BILLBOARD_STRIPE_WIDTH = 6;
  static readonly BILLBOARD_STRIPE_GAP = 6;
  static readonly BILLBOARD_MASK_ALPHA_THRESHOLD = 127;

  /*
   * BLACK BACKGROUND
   */
  static readonly BLACK_BACKGROUND_MATERIAL_COLOR = 0x000000;
  static readonly BLACK_BACKGROUND_MULTIPLIER = 10;
  static readonly BLACK_BACKGROUND_NAME = 'black_background';
  static readonly BLACK_BACKGROUND_POSITION_Y = -170;

  /*
   * BONE
   */

  static readonly BONE_THICKNESS = 1;
  static readonly BONE_THICKNESS_MULTIPLIER = 0.75;

  /*
   * BODY
   */

  static readonly BODY_MOVEMENT_BOUNDS = { min_x: -200, max_x: 200, min_z: 0, max_z: 0, min_y: 0.4 };
  static readonly BODY_SIZE_DEFAULT_VALUE = 50;
  static readonly DEFAULT_BODY_SIZE = 50;

  /*
   * BOUNDING BOX
   */
  static readonly BOUNDING_BOX_COLOR = 0xffffff;
  static readonly BOUNDING_BOX_NAME = 'movement_bounds_helper';

  /*
   * CAMERA
   */

  static readonly CAMERA_INIT_POSITION_X = 0;
  static readonly CAMERA_INIT_POSITION_Y = 150;
  static readonly CAMERA_INIT_POSITION_Z = 300;

  static readonly CAMERA_INIT_DISTANCE_BLOCKS = 8;
  static readonly CAMERA_INIT_HEIGHT_OFFSET_BLOCKS = 4;
  static readonly CAMERA_INIT_FAR = 10000;
  static readonly CAMERA_INIT_NEAR = 0.1;
  static readonly CAMERA_INIT_FOV = 60;

  static readonly CAMERA_WASD_MOVEMENT_SPEED_UNITS_PER_SECOND = 120;
  static readonly CAMERA_WASD_LOOK_SPEED_X = 2.0;
  static readonly CAMERA_WASD_LOOK_SPEED_Y = 2.0;
  static readonly CAMERA_WASD_WHEEL_SPEED = 1.0;

  static readonly CAMERA_WASD_MOUSE_HEIGHT_SPEED = 0.5;
  static readonly CAMERA_WASD_MOUSE_PAN_SPEED = 0.6;

  static readonly CAMERA_BOUNDS = {
    x: { min: -320, max: 320 },
    y: { min: 0, max: 640 },
    z: { min: -640, max: 320 },
  };

  /*
   * CLEAR COLOR
   */
  static readonly CLEAR_COLOR = 0xaaaaaa;

  /*
   * COLORS
   */
  static readonly COLOR_SELECTED_FOR_BONE = 0xeeee00;

  /*
   * CONNECT KEYPOINTS
   */
  static readonly CONNECT_KEYPOINTS = [
    [1, 2], // neck -> right_shoulder
    [1, 3], // neck -> left_shoulder
    [1, 5], // neck -> right_hip (через five)
    [2, 3], // right_shoulder -> left_shoulder
    [2, 4], // right_shoulder -> right_elbow
    [3, 4], // left_shoulder -> left_elbow
    [3, 5], // left_shoulder -> right_hip
    [5, 6], // right_hip -> right_knee
    [5, 7], // right_hip -> left_knee
    [4, 6], // right_elbow -> right_knee
    [6, 7], // right_knee -> left_knee
    [1, 8], // neck -> left_hip
    [8, 9], // left_hip -> left_knee
    [8, 10], // left_hip -> left_ankle
    [9, 10], // left_knee -> left_ankle
    [1, 11], // neck -> right_ankle
    [1, 9], // neck -> left_knee
    [10, 12], // left_ankle -> right_wrist
    [11, 12], // right_ankle -> right_wrist
    [9, 11], // left_knee -> right_ankle
    [12, 13], // right_wrist -> left_wrist
    [0, 1], // nose -> neck
    [0, 14], // nose -> right_eye
    [14, 16], // right_eye -> right_ear
    [11, 13], // right_ankle -> left_wrist
    [0, 15], // nose -> left_eye
    [15, 17], // left_eye -> left_ear
  ] as const;

  /*
   * CONNECTS COLORS
   */
  static readonly CONNECTS_COLORS = [
    [255, 85, 0],
    [255, 170, 0],
    [170, 255, 0],
    [85, 255, 0],
    [0, 255, 85],
    [0, 255, 170],
    [0, 255, 255],
    [0, 170, 255],
    [0, 85, 255],
    [85, 0, 255],
    [170, 0, 255],
    [255, 0, 255],
    [255, 0, 170],
    [255, 0, 85],
    [255, 0, 0],
    [255, 85, 0],
    [255, 170, 0],
    [170, 255, 0],
    [85, 255, 0],
    [0, 255, 85],
    [0, 255, 170],
    [0, 255, 255],
    [255, 170, 0],
    [255, 170, 0],
    [255, 170, 0],
    [255, 170, 0],
  ] as const;

  /*
   * CURSOR AXIS
   */
  static readonly CURSOR_AXIS_PLANE_RENDER_ORDER = 999;
  static readonly CURSOR_AXIS_RENDER_ORDER = 1000;

  /*
   * CURSOR PARTS
   */
  static readonly CURSOR_PART_CONE = '_CONE';
  static readonly CURSOR_PART_CYLINDER = '_CYLINDER';
  static readonly CURSOR_PART_PLANE = '_PLANE';

  /*
   * CURSOR PLANE
   */
  static readonly CURSOR_PLANE_LINE_RENDER_ORDER = 999;
  static readonly CURSOR_PLANE_RENDER_ORDER = 1000;

  static readonly CURSOR_PLANE_DEFAULT_OPACITY = 0.7;
  static readonly CURSOR_PLANE_UNHOVER_OPACITY = 0.3;
  static readonly CURSOR_PLANE_HOVER_OPACITY = 1.0;

  /*
   * DIRECTIONAL LIGHT
   */
  static readonly DIRECTIONSL_LIGHT_COLOR = 0xffffff;
  static readonly DIRECTIONSL_LIGHT_INTENSITY = 1.0;
  static readonly DIRECTIONAL_LIGHT_INIT_POSITION_X = 0;
  static readonly DIRECTIONAL_LIGHT_INIT_POSITION_Y = 160;
  static readonly DIRECTIONAL_LIGHT_INIT_POSITION_Z = 1000;

  /*
   * FOOT
   */
  static readonly FOOT_COLOR = 0xff0000;
  static readonly FOOT_MASK_COLOR = 0x000000;
  static readonly FOOT_SCALE = 0.25;

  /*
   * GRID HELPER
   */
  static readonly GRID_HELPER_DIVISIONS = 200;
  static readonly GRID_HELPER_SIZE = 8000;

  /*
   * HAND
   */
  static readonly HAND_COLOR = 0xff0000;
  static readonly HAND_MASK_COLOR = 0x000000;
  static readonly HAND_SCALE = 2.2;

  /*
   * INTERSECT TARGET
   */
  static readonly INTERSECT_TARGET_POINT_NAME = 'red_point';

  /*
   * IMPORTED MODEL PARTS
   */
  static OPEN_POSE_PART_FROM_FILE_FOOT: THREE.Group = new THREE.Group();
  static OPEN_POSE_PART_FROM_FILE_HAND: THREE.Group = new THREE.Group();

  /*
   * LINK (NAMES)
   */
  static readonly JOINT_SPHERE_NAME = '_joint_sphere';
  static readonly LINK_NAME = '_link_';

  /*
   * MODEL INFO
   */
  static readonly MODEL_INFO_FOOT = {
    mesh_name: 'FootObject',
    bone_prefix: 'FootBone',
  };
  static readonly MODEL_INFO_HAND = {
    mesh_name: 'shoupolySurface1',
    bone_prefix: 'shoujoint',
  };

  /*
   * OPEN POSE (ENUM LISTS)
   */
  static readonly OPEN_POSE_BODY_BONES_LIST: string[] = Object.values(EOpenPoseBodyBone);
  static readonly OPEN_POSE_BODY_GROUPS_LIST: string[] = Object.values(EOpenPoseBodyGroup);
  static readonly OPEN_POSE_BODY_NODES_LIST: string[] = Object.values(EOpenPoseBodyNode);

  /*
   * OPEN POSE (KEYPOINTS LIST)
   */
  static readonly OPEN_POSE_KEYPOINTS_LIST: string[] = [
    EOpenPoseBodyNode.NOSE,
    EOpenPoseBodyNode.NECK,
    EOpenPoseBodyNode.SHOULDER_RIGHT,
    EOpenPoseBodyNode.SHOULDER_LEFT,
    EOpenPoseBodyNode.ELBOW_RIGHT,
    EOpenPoseBodyNode.ELBOW_LEFT,
    EOpenPoseBodyNode.WRIST_RIGHT,
    EOpenPoseBodyNode.WRIST_LEFT,
    EOpenPoseBodyNode.HIP_RIGHT,
    EOpenPoseBodyNode.HIP_LEFT,
    EOpenPoseBodyNode.KNEE_RIGHT,
    EOpenPoseBodyNode.KNEE_LEFT,
    EOpenPoseBodyNode.ANKLE_RIGHT,
    EOpenPoseBodyNode.ANKLE_LEFT,
    EOpenPoseBodyNode.EYE_RIGHT,
    EOpenPoseBodyNode.EYE_LEFT,
    EOpenPoseBodyNode.EAR_RIGHT,
    EOpenPoseBodyNode.EAR_LEFT,
  ] as const;

  /*
   * PICKABLE PARTS
   */
  static readonly PICKABLE_PARTS_LIST: string[] = [
    EOpenPoseBodyGroup.TORSO,
    EOpenPoseBodyNode.NOSE,
    EOpenPoseBodyNode.NECK,
    EOpenPoseBodyNode.SHOULDER_RIGHT,
    EOpenPoseBodyNode.SHOULDER_LEFT,
    EOpenPoseBodyNode.ELBOW_RIGHT,
    EOpenPoseBodyNode.ELBOW_LEFT,
    EOpenPoseBodyNode.WRIST_RIGHT,
    EOpenPoseBodyNode.WRIST_LEFT,
    EOpenPoseBodyNode.HIP_RIGHT,
    EOpenPoseBodyNode.HIP_LEFT,
    EOpenPoseBodyNode.KNEE_RIGHT,
    EOpenPoseBodyNode.KNEE_LEFT,
    EOpenPoseBodyNode.ANKLE_RIGHT,
    EOpenPoseBodyNode.ANKLE_LEFT,
    EOpenPoseBodyNode.EYE_LEFT,
    EOpenPoseBodyNode.EYE_RIGHT,
    EOpenPoseBodyNode.EAR_LEFT,
    EOpenPoseBodyNode.EAR_RIGHT,
  ] as const;

  /*
   * PLANE CURSOR
   */
  static readonly PLANE_CURSOR_AXIS_HINT_LENGTH = 0.6;
  static readonly PLANE_CURSOR_NAME = 'PlaneCursor';
  static readonly PLANE_CURSOR_PLANE_SIZE_WORLD = 10;
  static readonly PLANE_CURSOR_SIZE_INNER_MULTIPLIER = 0.8;
  static readonly PLANE_CURSOR_SMALL_OFFSET = 0.15;
  static readonly PLANE_CURSOR_XY_PLANE_COLOR = 0x4444ff;
  static readonly PLANE_CURSOR_XZ_PLANE_COLOR = 0x44ff44;
  static readonly PLANE_CURSOR_YZ_PLANE_COLOR = 0xff4444;

  /*
   * PROJECTION BILLBOARD
   */
  static readonly PROJECTION_BILLBOARD_NAME = 'projection_billboard';
  static readonly PROJECTION_BILLBOARD_POSITION_Y = -155;

  /*
   * RESOLUTION
   */
  static readonly RESOLUTION_FOR_BODY_PROJECTION = 1024;

  /*
   * SCENE GRID
   */
  static readonly SCENE_GRID_BLOCK_SIZE = 40;

  /*
   * SELECTED NODE
   */
  static readonly SELECTED_NODE_SCALE_DIVISOR = 1.5;

  /*
   * TRNSFORM CONTROLS
   */

  static readonly TRANSFORM_CONTROLS_SIZE = 0.4;

  /*
   * COMPLEX OBJECTS
   */

  static readonly BODY_CONNECTIONS: Array<[string, string]> = [
    [EOpenPoseBodyNode.NECK, EOpenPoseBodyNode.NOSE],

    [EOpenPoseBodyNode.NOSE, EOpenPoseBodyNode.EYE_LEFT],
    [EOpenPoseBodyNode.NOSE, EOpenPoseBodyNode.EYE_RIGHT],

    [EOpenPoseBodyNode.EYE_LEFT, EOpenPoseBodyNode.EAR_LEFT],
    [EOpenPoseBodyNode.EYE_RIGHT, EOpenPoseBodyNode.EAR_RIGHT],

    [EOpenPoseBodyNode.NECK, EOpenPoseBodyNode.SHOULDER_LEFT],
    [EOpenPoseBodyNode.NECK, EOpenPoseBodyNode.SHOULDER_RIGHT],

    [EOpenPoseBodyNode.NECK, EOpenPoseBodyNode.HIP_RIGHT],
    [EOpenPoseBodyNode.NECK, EOpenPoseBodyNode.HIP_LEFT],

    [EOpenPoseBodyNode.SHOULDER_LEFT, EOpenPoseBodyNode.ELBOW_LEFT],
    [EOpenPoseBodyNode.ELBOW_LEFT, EOpenPoseBodyNode.WRIST_LEFT],

    [EOpenPoseBodyNode.SHOULDER_RIGHT, EOpenPoseBodyNode.ELBOW_RIGHT],
    [EOpenPoseBodyNode.ELBOW_RIGHT, EOpenPoseBodyNode.WRIST_RIGHT],

    [EOpenPoseBodyNode.HIP_LEFT, EOpenPoseBodyNode.KNEE_LEFT],
    [EOpenPoseBodyNode.KNEE_LEFT, EOpenPoseBodyNode.ANKLE_LEFT],

    [EOpenPoseBodyNode.HIP_RIGHT, EOpenPoseBodyNode.KNEE_RIGHT],
    [EOpenPoseBodyNode.KNEE_RIGHT, EOpenPoseBodyNode.ANKLE_RIGHT],
  ];

  static readonly BONE_DISPLAY_COLORS: Record<string, number> = {
    [`${EOpenPoseBodyNode.NECK}${this.LINK_NAME}${EOpenPoseBodyNode.NOSE}`]: to_hex_color([0, 0, 255]),
    [`${EOpenPoseBodyNode.NOSE}${this.LINK_NAME}${EOpenPoseBodyNode.EYE_LEFT}`]: to_hex_color([85, 0, 255]),
    [`${EOpenPoseBodyNode.NOSE}${this.LINK_NAME}${EOpenPoseBodyNode.EYE_RIGHT}`]: to_hex_color([170, 0, 255]),
    [`${EOpenPoseBodyNode.EYE_LEFT}${this.LINK_NAME}${EOpenPoseBodyNode.EAR_LEFT}`]: to_hex_color([255, 0, 255]),
    [`${EOpenPoseBodyNode.EYE_RIGHT}${this.LINK_NAME}${EOpenPoseBodyNode.EAR_RIGHT}`]: to_hex_color([255, 0, 170]),

    [`${EOpenPoseBodyNode.NECK}${this.LINK_NAME}${EOpenPoseBodyNode.SHOULDER_LEFT}`]: to_hex_color([255, 85, 0]),
    [`${EOpenPoseBodyNode.NECK}${this.LINK_NAME}${EOpenPoseBodyNode.SHOULDER_RIGHT}`]: to_hex_color([255, 0, 0]),

    [`${EOpenPoseBodyNode.SHOULDER_LEFT}${this.LINK_NAME}${EOpenPoseBodyNode.ELBOW_LEFT}`]: to_hex_color([255, 170, 0]),
    [`${EOpenPoseBodyNode.ELBOW_LEFT}${this.LINK_NAME}${EOpenPoseBodyNode.WRIST_LEFT}`]: to_hex_color([255, 255, 0]),
    [`${EOpenPoseBodyNode.SHOULDER_RIGHT}${this.LINK_NAME}${EOpenPoseBodyNode.ELBOW_RIGHT}`]: to_hex_color([170, 255, 0]),
    [`${EOpenPoseBodyNode.ELBOW_RIGHT}${this.LINK_NAME}${EOpenPoseBodyNode.WRIST_RIGHT}`]: to_hex_color([85, 255, 0]),

    [`${EOpenPoseBodyNode.HIP_LEFT}${this.LINK_NAME}${EOpenPoseBodyNode.KNEE_LEFT}`]: to_hex_color([0, 255, 255]),
    [`${EOpenPoseBodyNode.KNEE_LEFT}${this.LINK_NAME}${EOpenPoseBodyNode.ANKLE_LEFT}`]: to_hex_color([0, 170, 255]),
    [`${EOpenPoseBodyNode.HIP_RIGHT}${this.LINK_NAME}${EOpenPoseBodyNode.KNEE_RIGHT}`]: to_hex_color([0, 85, 255]),
    [`${EOpenPoseBodyNode.KNEE_RIGHT}${this.LINK_NAME}${EOpenPoseBodyNode.ANKLE_RIGHT}`]: to_hex_color([0, 0, 255]),
  };

  static readonly BONE_PARENT_MAP: Record<string, string> = {
    // Arms
    [EOpenPoseBodyNode.ELBOW_LEFT]: EOpenPoseBodyNode.SHOULDER_LEFT,
    [EOpenPoseBodyNode.ELBOW_RIGHT]: EOpenPoseBodyNode.SHOULDER_RIGHT,
    [EOpenPoseBodyNode.WRIST_LEFT]: EOpenPoseBodyNode.ELBOW_LEFT,
    [EOpenPoseBodyNode.WRIST_RIGHT]: EOpenPoseBodyNode.ELBOW_RIGHT,

    // Legs
    [EOpenPoseBodyNode.KNEE_LEFT]: EOpenPoseBodyNode.HIP_LEFT,
    [EOpenPoseBodyNode.KNEE_RIGHT]: EOpenPoseBodyNode.HIP_RIGHT,
    [EOpenPoseBodyNode.ANKLE_LEFT]: EOpenPoseBodyNode.KNEE_LEFT,
    [EOpenPoseBodyNode.ANKLE_RIGHT]: EOpenPoseBodyNode.KNEE_RIGHT,

    // Head and face
    [EOpenPoseBodyNode.NOSE]: EOpenPoseBodyNode.NECK,
    [EOpenPoseBodyNode.EYE_LEFT]: EOpenPoseBodyNode.NOSE,
    [EOpenPoseBodyNode.EYE_RIGHT]: EOpenPoseBodyNode.NOSE,
    [EOpenPoseBodyNode.EAR_LEFT]: EOpenPoseBodyNode.EYE_LEFT,
    [EOpenPoseBodyNode.EAR_RIGHT]: EOpenPoseBodyNode.EYE_RIGHT,

    // Shoulders - connect to neck
    [EOpenPoseBodyNode.SHOULDER_LEFT]: EOpenPoseBodyNode.NECK,
    [EOpenPoseBodyNode.SHOULDER_RIGHT]: EOpenPoseBodyNode.NECK,

    [EOpenPoseBodyNode.NECK]: EOpenPoseBodyGroup.TORSO,

    // Hands and feet
    [EOpenPoseBodyNode.HAND_LEFT]: EOpenPoseBodyNode.WRIST_LEFT,
    [EOpenPoseBodyNode.HAND_RIGHT]: EOpenPoseBodyNode.WRIST_RIGHT,
    [EOpenPoseBodyNode.FOOT_LEFT]: EOpenPoseBodyNode.ANKLE_LEFT,
    [EOpenPoseBodyNode.FOOT_RIGHT]: EOpenPoseBodyNode.ANKLE_RIGHT,
  };

  static readonly BONE_CONNECTIONS: Array<[string, string, EOpenPoseBodyBone]> = [
    [EOpenPoseBodyNode.NECK, EOpenPoseBodyNode.NOSE, EOpenPoseBodyBone.NECK_TO_NOSE],
    [EOpenPoseBodyNode.NOSE, EOpenPoseBodyNode.EYE_LEFT, EOpenPoseBodyBone.NOSE_TO_EYE_LEFT],
    [EOpenPoseBodyNode.NOSE, EOpenPoseBodyNode.EYE_RIGHT, EOpenPoseBodyBone.NOSE_TO_EYE_RIGHT],

    [EOpenPoseBodyNode.EYE_LEFT, EOpenPoseBodyNode.EAR_LEFT, EOpenPoseBodyBone.EYE_TO_EAR_LEFT],
    [EOpenPoseBodyNode.EYE_RIGHT, EOpenPoseBodyNode.EAR_RIGHT, EOpenPoseBodyBone.EYE_TO_EAR_RIGHT],

    [EOpenPoseBodyNode.NECK, EOpenPoseBodyNode.SHOULDER_LEFT, EOpenPoseBodyBone.SHOULDER_LEFT],
    [EOpenPoseBodyNode.NECK, EOpenPoseBodyNode.SHOULDER_RIGHT, EOpenPoseBodyBone.SHOULDER_RIGHT],

    [EOpenPoseBodyNode.SHOULDER_LEFT, EOpenPoseBodyNode.ELBOW_LEFT, EOpenPoseBodyBone.UPPER_ARM_LEFT],
    [EOpenPoseBodyNode.ELBOW_LEFT, EOpenPoseBodyNode.WRIST_LEFT, EOpenPoseBodyBone.FOREARM_LEFT],

    [EOpenPoseBodyNode.SHOULDER_RIGHT, EOpenPoseBodyNode.ELBOW_RIGHT, EOpenPoseBodyBone.UPPER_ARM_RIGHT],
    [EOpenPoseBodyNode.ELBOW_RIGHT, EOpenPoseBodyNode.WRIST_RIGHT, EOpenPoseBodyBone.FOREARM_RIGHT],

    [EOpenPoseBodyNode.NECK, EOpenPoseBodyNode.HIP_LEFT, EOpenPoseBodyBone.THIGH_LEFT],
    [EOpenPoseBodyNode.NECK, EOpenPoseBodyNode.HIP_RIGHT, EOpenPoseBodyBone.THIGH_RIGHT],

    [EOpenPoseBodyNode.HIP_LEFT, EOpenPoseBodyNode.KNEE_LEFT, EOpenPoseBodyBone.UPPER_LEG_LEFT],
    [EOpenPoseBodyNode.KNEE_LEFT, EOpenPoseBodyNode.ANKLE_LEFT, EOpenPoseBodyBone.LOWER_LEG_LEFT],

    [EOpenPoseBodyNode.HIP_RIGHT, EOpenPoseBodyNode.KNEE_RIGHT, EOpenPoseBodyBone.UPPER_LEG_RIGHT],
    [EOpenPoseBodyNode.KNEE_RIGHT, EOpenPoseBodyNode.ANKLE_RIGHT, EOpenPoseBodyBone.LOWER_LEG_RIGHT],
  ];

  static readonly CONTROLABLE_PARTS_LIST: string[] = [
    ...this.OPEN_POSE_KEYPOINTS_LIST,
    EOpenPoseBodyNode.HAND_RIGHT,
    EOpenPoseBodyNode.HAND_LEFT,
    EOpenPoseBodyNode.FOOT_RIGHT,
    EOpenPoseBodyNode.FOOT_LEFT,
  ] as const;
}
